import { REGION } from '@env';
import { format } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { ISnListQueryParameters } from '@lib/api';
import { SnCurrentOrganization } from '@lib/auth/services';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { LprIntegrationType } from '@lib/data-dog-rum/custom-action-map';

import { IFsLprFlockParameters } from '../api/interfaces/lpr-flock-parameters.interface';
import { FlockLprResults } from '../api/interfaces/lpr-flock-results';
import { SnLprFlockRead } from '../api/models/lpr-flock-read';
import {
  extractLprErrorCode,
  extractLprRawError,
  parseLprApiError,
} from '../utils/lpr-error-utils';

@Injectable({
  providedIn: 'root',
})
export class FlockLprService implements OnDestroy {
  public flockNextPage;
  public flockPreviousPage;
  public flockResults = [];

  private readonly _destroy$ = new Subject<void>();
  private readonly _integrationTypeV1: LprIntegrationType = 'flock_v1';
  private readonly _integrationTypeV2: LprIntegrationType = 'flock';

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _toastrService: ToastrService,
    private readonly _metrics: FsDataDogRumService,
  ) {}

  get region(): string {
    return REGION;
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // TODO - FWX-1150 Refactor and remove getFlockPlates() after the new ALPR Search is rolled out
  public getFlockPlates(params: ISnListQueryParameters): Observable<any> {
    const startTime = Date.now();
    let fromDate;
    let toDate;
    if (params.event_timestamp__gte || params.event_timestamp__lte) {
      if (this.region !== 'US') {
        fromDate = params.event_timestamp__gte
          ? format(
              new Date(parseInt(params.event_timestamp__gte, 10)).getTime() * 1000,
              "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX",
            ).replace('+', '-')
          : null;
        toDate = params.event_timestamp__lte
          ? format(
              new Date(parseInt(params.event_timestamp__lte, 10)).getTime() * 1000,
              "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX",
            ).replace('+', '-')
          : null;
      } else {
        fromDate = params.event_timestamp__gte
          ? format(
              new Date(parseInt(params.event_timestamp__gte, 10)).getTime() * 1000,
              "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX",
            )
          : null;
        toDate = params.event_timestamp__lte
          ? format(
              new Date(parseInt(params.event_timestamp__lte, 10)).getTime() * 1000,
              "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX",
            )
          : null;
      }
    } else if (params.event_timestamp_from || params.event_timestamp_to) {
      if (this.region !== 'US') {
        fromDate = params.event_timestamp_from
          ? format(new Date(params.event_timestamp_from), "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX").replace(
              '+',
              '-',
            )
          : null;
        toDate = params.event_timestamp_to
          ? format(new Date(params.event_timestamp_to), "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX").replace(
              '+',
              '-',
            )
          : null;
      } else {
        fromDate = params.event_timestamp_from
          ? format(new Date(params.event_timestamp_from), "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX")
          : null;
        toDate = params.event_timestamp_to
          ? format(new Date(params.event_timestamp_to), "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX")
          : null;
      }
    } else {
      fromDate = null;
      toDate = null;
    }
    const radius = +params.dist || Math.round(25 * 1609.344);
    const point = params.point || this._currentOrganization.settings?.geometry?.coordinates?.join();
    let plate;
    if (params.plate) {
      plate = params.plate;
    } else if (params.plate__startswith) {
      plate = `${params.plate__startswith}*`;
    } else if (params.plate__contains__in) {
      plate = `*${params.plate__contains__in}*`;
    } else if (params.plate__endswith) {
      plate = `*${params.plate__endswith}`;
    } else if (params.plateSearch) {
      plate = params.plateSearch;
    } else {
      plate = '*';
    }

    const filterMake =
      params.details__vehicleMake__in?.toLowerCase() ||
      params.vehicle_make?.toString().toLowerCase();
    const filterColor =
      params.details__vehicleColor__in?.toLowerCase() ||
      params.vehicle_color?.toString().toLowerCase();

    if (params.nextFlockPage) {
      this.flockNextPage = params.nextFlockPage;
    }

    if (point && radius && !params.fs_bbox) {
      const points = point.split(',');
      const cir = new google.maps.Circle({
        center: new google.maps.LatLng({
          lat: +points[1],
          lng: +points[0],
        }),
        radius,
      });
      const bounds = cir.getBounds();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      params.fs_bbox = `${sw.lng()},${ne.lat()},${ne.lng()},${sw.lat()}`;
    }

    const camQuery = params.fs_bbox
      ? {
          in_bbox: params.fs_bbox,
        }
      : {
          cameras: params.cameras,
        };
    let payload = !this.flockNextPage
      ? {
          plate,
          color: params.filterColor,
          make: params.filterMake,
          from_date: fromDate,
          to_date: toDate,
          pageSize: '100',
        }
      : {
          page: this.flockNextPage,
        };

    if (!params.omitPosition) {
      payload = {
        ...camQuery,
        ...payload,
      };
    }

    const lastPage = this.flockPreviousPage && !this.flockNextPage;
    if (lastPage) {
      return of({ count: 0, results: [] });
    }
    return this._httpClient
      .get(params.nextFlockPage ? params.nextFlockPage : '/api/integrations/flock/plate_search/', {
        params: params.nextFlockPage ? {} : payload,
        headers: {
          'X-fs-skip-error': 'true',
        },
      })
      .pipe(
        map((response: any) => {
          const result = {
            count: response.count || response.results?.length,
            totalPages: response.totalPages,
            results: response.results
              .map((read) => new SnLprFlockRead(read))
              .filter((r) => {
                let pass = true;
                if (filterMake) {
                  pass = r.vehicleMake?.toLowerCase().includes(filterMake);
                }
                if (filterColor) {
                  pass = pass && r.color?.toLowerCase().includes(filterColor);
                }
                return pass;
              }),
            links: response.links,
          };

          this._metrics.addAction('lpr_search', {
            status: response.status || 200,
            duration_ms: Date.now() - startTime,
            integration: this._integrationTypeV1,
          });

          return result;
        }),
        tap((flock) => {
          this.flockNextPage = flock?.links?.next ? flock.links.next.split('=')[1] : null;
          this.flockPreviousPage = flock?.links?.previous;
          this.flockResults = [...flock.results];
        }),
        catchError((error) => {
          this._metrics.addAction('lpr_search_error', {
            status: error?.status || 500,
            duration_ms: Date.now() - startTime,
            integration: this._integrationTypeV1,
            raw_error: extractLprRawError(error),
            error_code: extractLprErrorCode(error),
            error_message: parseLprApiError(error),
          });

          this._toastrService.error('Error loading more flock results');
          return of({ count: 0, results: [] });
        }),
      );
  }

  /**
   * Similar to getFlockPlates but used in ALPR V2 with logic moved from the service to
   * {@link FlockProviderService}.
   * @param params are the params required by the Flock API of the type {@link IFsLprFlockParameters}
   * @returns Response from the API of type {@link FlockLprResults}
   */
  public getFlockPlatesV2(params: Partial<IFsLprFlockParameters>): Observable<FlockLprResults> {
    // The reason for creating this new method is because the getFlockPlates() method is used in a couple of
    // different places needs to be refactored/ removed which is out of scope of this PR. Added a TODO to
    // refactor/ remove the getFlockPlates() method after the new ALPR Search is rolled out.
    const startTime = Date.now();
    return this._httpClient
      .get<FlockLprResults>('/api/integrations/flock/plate_search/', {
        params: params,
        headers: {
          'X-fs-skip-error': 'true',
        },
      })
      .pipe(
        map((response: FlockLprResults) => {
          if (Array.isArray(response.results)) {
            response.results = response.results.map((result) => new SnLprFlockRead(result));
          }
          this._metrics.addAction('lpr_search', {
            status: response.status || 200,
            duration_ms: Date.now() - startTime,
            integration: this._integrationTypeV2,
          });
          return response;
        }),
        catchError((error) => {
          this._metrics.addAction('lpr_search_error', {
            status: error?.status || 500,
            duration_ms: Date.now() - startTime,
            integration: this._integrationTypeV2,
            raw_error: extractLprRawError(error),
            error_code: extractLprErrorCode(error),
            error_message: parseLprApiError(error),
          });
          return throwError(error);
        }),
      );
  }

  public destroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this.flockNextPage = null;
    this.flockPreviousPage = null;
    this.flockResults = [];
  }
}
